import React, { useMemo, useState, useEffect } from 'react';
import { Icons } from '../Icons';
import { Patient, Appointment, PanoramicAnalysis, PanoramicFinding, PanoramicStudy } from '../../types';
import { APPT_STATUS } from '../../constants/appointmentStatus';

interface PatientRegistryProps {
  patients: Patient[];
  appointments: Appointment[];
  selectedPatientId: string | null;
  onSelectPatient: (patientId: string) => void;
  onEditPatient: (patient: Patient) => void;
  panoramicAnalyses: PanoramicAnalysis[];
  onDeletePatient?: (patientId: string) => void;
}

export const PatientRegistry: React.FC<PatientRegistryProps> = ({
  patients,
  appointments,
  selectedPatientId,
  onSelectPatient,
  onEditPatient,
  panoramicAnalyses,
  onDeletePatient,
}) => {
  const [sortBy, setSortBy] = useState<'name' | 'phone' | 'alerts'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [layout, setLayout] = useState<'list' | 'grid'>('list');
  const [page, setPage] = useState(1);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [uploadError, setUploadError] = useState<string>('');
  const [uploadedStudies, setUploadedStudies] = useState<Record<string, PanoramicStudy>>({});
  const pageSize = 10;

  const renderSortIcon = (field: typeof sortBy) => {
    if (sortBy !== field) return '⇅';
    return sortDir === 'asc' ? '▲' : '▼';
  };

  const sortedPatients = useMemo(() => {
    const arr = [...patients];
    arr.sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      if (sortBy === 'name') {
        return a.name.localeCompare(b.name) * dir;
      }
      if (sortBy === 'phone') {
        return a.phone.localeCompare(b.phone) * dir;
      }
      // alerts
      return (a.alerts.length - b.alerts.length) * dir;
    });
    return arr;
  }, [patients, sortBy, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedPatients.length / pageSize));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pagedPatients = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedPatients.slice(start, start + pageSize);
  }, [sortedPatients, page]);

  const selectedPatient = patients.find(p => p.id === selectedPatientId) ?? patients[0];

  const patientAppointments = useMemo(() => {
    if (!selectedPatient) return [];
    return appointments
      .filter(a => a.patientId === selectedPatient.id)
      .sort((a, b) => {
        const aDate = new Date(`${a.date}T${a.time}`);
        const bDate = new Date(`${b.date}T${b.time}`);
        return aDate.getTime() - bDate.getTime();
      });
  }, [appointments, selectedPatient]);

  const { upcomingAppointments, pastAppointments } = useMemo(() => {
    const now = new Date();
    const upcoming = patientAppointments.filter(a => new Date(`${a.date}T${a.time}`) >= now);
    const past = patientAppointments.filter(a => new Date(`${a.date}T${a.time}`) < now).reverse();
    return {
      upcomingAppointments: upcoming.slice(0, 3),
      pastAppointments: past.slice(0, 3),
    };
  }, [patientAppointments]);

  const selectedAnalysis = useMemo(() => {
    if (!selectedPatient) return null;
    return panoramicAnalyses.find(entry => entry.patientId === selectedPatient.id) ?? null;
  }, [panoramicAnalyses, selectedPatient]);

  const latestStudy = useMemo(() => {
    const stored = selectedAnalysis?.studies ?? [];
    const uploaded = selectedPatient ? uploadedStudies[selectedPatient.id] : undefined;
    const allStudies = uploaded ? [...stored, uploaded] : stored;
    if (!allStudies.length) return null;
    return [...allStudies].sort((a, b) => b.capturedAt.localeCompare(a.capturedAt))[0];
  }, [selectedAnalysis, selectedPatient, uploadedStudies]);

  const diagnosisSummary = useMemo(() => {
    if (!latestStudy) return [];
    const counts = latestStudy.findings.reduce<Record<string, number>>((acc, finding) => {
      acc[finding.diagnosis] = (acc[finding.diagnosis] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [latestStudy]);

  const topFindings = useMemo(() => {
    if (!latestStudy) return [];
    return [...latestStudy.findings].sort((a, b) => b.area - a.area).slice(0, 6);
  }, [latestStudy]);

  const handlePanoramicUpload = async () => {
    if (!selectedPatient) return;
    const token = import.meta.env.VITE_HF_TOKEN as string | undefined;
    const endpoint =
      (import.meta.env.VITE_HF_MODEL_URL as string | undefined) ||
      'https://api-inference.huggingface.co/models/SerdarHelli/Segmentation-of-Teeth-in-Panoramic-X-ray-Image-Using-U-Net';

    if (!token) {
      setUploadStatus('error');
      setUploadError('Missing VITE_HF_TOKEN. Add your Hugging Face token to .env.');
      return;
    }
    if (!uploadFile) {
      setUploadStatus('error');
      setUploadError('Select a panoramic X-ray image first.');
      return;
    }

    setUploadStatus('uploading');
    setUploadError('');

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: uploadFile,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Inference request failed.');
      }

      const payload = await response.json();
      if (payload?.error) {
        throw new Error(payload.error);
      }

      const findings: PanoramicFinding[] = [];
      if (Array.isArray(payload)) {
        payload.forEach((item: { label?: string; score?: number }, idx: number) => {
          findings.push({
            id: `live-${Date.now()}-${idx}`,
            fdiTooth: 'N/A',
            quadrant: 'N/A',
            tooth: 'N/A',
            diagnosis: 'Segmentation',
            bbox: [0, 0, 0, 0],
            area: 0,
            score: item.score,
          });
        });
      } else if (payload?.mask) {
        findings.push({
          id: `live-${Date.now()}-0`,
          fdiTooth: 'N/A',
          quadrant: 'N/A',
          tooth: 'N/A',
          diagnosis: 'Segmentation',
          bbox: [0, 0, 0, 0],
          area: 0,
        });
      }

      const now = new Date().toISOString().split('T')[0];
      const newStudy: PanoramicStudy = {
        id: `pan-live-${Date.now()}`,
        imageId: Date.now(),
        fileName: uploadFile.name,
        width: 0,
        height: 0,
        capturedAt: now,
        findings,
        source: {
          dataset: 'Hugging Face Inference API',
          url: endpoint,
          license: 'Model-specific',
        },
      };

      setUploadedStudies(prev => ({ ...prev, [selectedPatient.id]: newStudy }));
      setUploadStatus('success');
      setUploadFile(null);
    } catch (err) {
      setUploadStatus('error');
      setUploadError(err instanceof Error ? err.message : 'Upload failed.');
    }
  };

  if (!selectedPatient) {
    return (
      <div className="space-y-4">
        <h2 className="text-3xl font-black">Patient Registry</h2>
        <div className="p-8 bg-white rounded-3xl border shadow-sm text-slate-500">
          No patients found. Use “Add Patient” to create one.
        </div>
      </div>
    );
  }

  const renderAppointmentCard = (appt: Appointment) => {
    const status = APPT_STATUS[appt.status];
    return (
      <div key={appt.id} className="p-3 bg-slate-50 rounded-2xl flex items-center justify-between">
        <div>
          <p className="font-bold text-sm">{appt.type}</p>
          <p className="text-xs text-slate-500">
            {appt.date} at {appt.time} · {appt.room}
          </p>
        </div>
        <span className={`text-[10px] font-bold px-2 py-1 rounded-lg ${status.color}`}>
          {status.label}
        </span>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      <h2 className="text-3xl font-black">My Registry</h2>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex gap-2 bg-slate-100 rounded-xl p-1 w-fit">
          {(['list', 'grid'] as const).map(mode => (
            <button
              key={mode}
              onClick={() => setLayout(mode)}
              className={`px-3 py-2 rounded-lg text-xs font-bold capitalize transition-all ${layout === mode ? 'bg-white shadow-sm' : 'text-slate-500'
                }`}
            >
              {mode}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <button
            disabled={page === 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
            className="px-3 py-2 rounded-lg border disabled:opacity-50"
          >
            Prev
          </button>
          <span>
            Page {page} / {totalPages}
          </span>
          <button
            disabled={page === totalPages}
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            className="px-3 py-2 rounded-lg border disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1.6fr_1fr] gap-6">
        {/* Patient list */}
        <div className="bg-white rounded-3xl border shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            {layout === 'list' ? (
              <table className="w-full min-w-[640px]">
                <thead className="bg-slate-50 border-b-2 border-slate-200">
                  <tr className="text-xs font-black text-slate-700 uppercase tracking-wider">
                    <th
                      className="px-8 py-5 text-left cursor-pointer select-none"
                      onClick={() => {
                        setSortBy('name');
                        setSortDir(prev => (sortBy === 'name' && prev === 'asc' ? 'desc' : 'asc'));
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <span>Name</span>
                        <span className="text-[10px]">{renderSortIcon('name')}</span>
                      </div>
                    </th>
                    <th
                      className="px-8 py-5 text-left cursor-pointer select-none"
                      onClick={() => {
                        setSortBy('phone');
                        setSortDir(prev => (sortBy === 'phone' && prev === 'asc' ? 'desc' : 'asc'));
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <span>Phone</span>
                        <span className="text-[10px]">{renderSortIcon('phone')}</span>
                      </div>
                    </th>
                    <th
                      className="px-8 py-5 text-left cursor-pointer select-none"
                      onClick={() => {
                        setSortBy('alerts');
                        setSortDir(prev => (sortBy === 'alerts' && prev === 'asc' ? 'desc' : 'asc'));
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <span>Alerts</span>
                        <span className="text-[10px]">{renderSortIcon('alerts')}</span>
                      </div>
                    </th>
                    <th className="px-8 py-5 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {pagedPatients.map(p => (
                    <tr
                      key={p.id}
                      onClick={() => onSelectPatient(p.id)}
                      className={`hover:bg-slate-50 cursor-pointer transition-colors ${selectedPatient?.id === p.id ? 'bg-blue-50/60 border-l-4 border-blue-500' : ''
                        }`}
                    >
                      <td className="px-8 py-6">
                        <p className="font-bold">{p.name}</p>
                        <p className="text-xs text-slate-400">{p.email}</p>
                      </td>
                      <td className="px-8 py-6 text-sm">{p.phone}</td>
                      <td className="px-8 py-6">
                        {p.alerts.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {p.alerts.map(a => (
                              <span key={a} className="px-2 py-1 bg-red-100 text-red-600 text-xs rounded">
                                {a}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-slate-300 text-xs">None</span>
                        )}
                      </td>
                      <td className="px-8 py-6 text-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditPatient(p);
                          }}
                          className="p-2 text-slate-400 hover:text-blue-600 transition-colors"
                          title="Edit Patient"
                        >
                          <Icons.Edit className="w-4 h-4" />
                        </button>
                        {onDeletePatient && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeletePatient(p.id);
                            }}
                            className="p-2 text-slate-400 hover:text-rose-600 transition-colors"
                            title="Delete Patient"
                          >
                            <Icons.Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {pagedPatients.map(p => (
                  <div
                    key={p.id}
                    onClick={() => onSelectPatient(p.id)}
                    className={`rounded-2xl border-2 p-4 cursor-pointer transition-all ${selectedPatient?.id === p.id ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:border-blue-300'
                      }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-bold text-lg">{p.name}</p>
                        <p className="text-xs text-slate-400">{p.email}</p>
                        <p className="text-sm text-slate-600 mt-1">{p.phone}</p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditPatient(p);
                        }}
                        className="p-2 text-slate-400 hover:text-blue-600"
                      >
                        <Icons.Edit className="w-4 h-4" />
                      </button>
                      {onDeletePatient && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeletePatient(p.id);
                          }}
                          className="p-2 text-slate-400 hover:text-rose-600 transition-colors"
                          title="Delete Patient"
                        >
                          <Icons.Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-1">
                      {p.alerts.length > 0 ? (
                        p.alerts.map(a => (
                          <span key={a} className="px-2 py-1 bg-red-100 text-red-600 text-xs rounded">
                            {a}
                          </span>
                        ))
                      ) : (
                        <span className="text-slate-300 text-xs">No alerts</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Patient profile summary */}
        <div className="space-y-4">
          <div className="bg-white rounded-3xl border shadow-sm p-6 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase">Patient Profile</p>
                <h3 className="text-xl font-black mt-1">{selectedPatient.name}</h3>
                <p className="text-sm text-slate-500">{selectedPatient.email}</p>
                <p className="text-sm text-slate-500">{selectedPatient.phone}</p>
              </div>
              <button
                onClick={() => onEditPatient(selectedPatient)}
                className="px-3 py-2 bg-blue-600 text-white rounded-xl text-xs font-bold shadow-sm"
              >
                Edit
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="p-3 bg-slate-50 rounded-2xl">
                <p className="text-xs text-slate-400 font-bold uppercase">Insurance</p>
                <p className="font-bold text-slate-800">{selectedPatient.insurance || 'Self-pay'}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-2xl">
                <p className="text-xs text-slate-400 font-bold uppercase">Alerts</p>
                <p className="font-bold text-slate-800">{selectedPatient.alerts.length || 'None'}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-2xl col-span-2">
                <p className="text-xs text-slate-400 font-bold uppercase">Notes</p>
                <p className="font-medium text-slate-700 max-h-20 overflow-hidden">
                  {selectedPatient.dentalHistory || 'No notes on file yet.'}
                </p>
              </div>
            </div>

            {selectedPatient.attachments.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-slate-400 font-bold uppercase">Attachments</p>
                <div className="flex flex-wrap gap-2">
                  {selectedPatient.attachments.slice(0, 3).map(file => (
                    <span
                      key={file.id}
                      className="inline-flex items-center space-x-2 px-3 py-2 bg-slate-50 rounded-xl text-xs font-bold text-slate-700"
                    >
                      <Icons.Paperclip />
                      <span className="truncate max-w-[140px]">{file.name}</span>
                    </span>
                  ))}
                  {selectedPatient.attachments.length > 3 && (
                    <span className="text-xs font-bold text-blue-600">
                      +{selectedPatient.attachments.length - 3} more
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div className="bg-white rounded-3xl border shadow-sm p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-slate-400 uppercase">Upcoming Appointments</p>
                  <p className="font-black text-lg">{upcomingAppointments.length || 0}</p>
                </div>
                <Icons.Calendar />
              </div>
              {upcomingAppointments.length === 0 ? (
                <p className="text-sm text-slate-400">No upcoming appointments.</p>
              ) : (
                <div className="space-y-2">
                  {upcomingAppointments.map(renderAppointmentCard)}
                </div>
              )}
            </div>

            <div className="bg-white rounded-3xl border shadow-sm p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-slate-400 uppercase">Past Appointments</p>
                  <p className="font-black text-lg">{pastAppointments.length || 0}</p>
                </div>
                <Icons.Clock />
              </div>
              {pastAppointments.length === 0 ? (
                <p className="text-sm text-slate-400">No past appointments recorded.</p>
              ) : (
                <div className="space-y-2">
                  {pastAppointments.map(renderAppointmentCard)}
                </div>
              )}
            </div>

            <div className="bg-white rounded-3xl border shadow-sm p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-slate-400 uppercase">Clinical Memory</p>
                  <p className="font-black text-lg">Dental & Visit History</p>
                </div>
                <Icons.Stethoscope />
              </div>

              <div className="space-y-2">
                <p className="text-xs text-slate-400 font-bold uppercase">Past visits</p>
                {pastAppointments.length === 0 ? (
                  <p className="text-xs text-slate-400">No historical visits</p>
                ) : (
                  <div className="space-y-2">
                    {pastAppointments.slice(0, 5).map(appt => (
                      <div key={appt.id} className="p-3 rounded-xl bg-slate-50 border flex justify-between items-start">
                        <div>
                          <p className="text-sm font-bold">{appt.date} · {appt.type}</p>
                          <p className="text-xs text-slate-500">Outcome: {appt.status}</p>
                          {appt.internalNotes && (
                            <p className="text-[11px] text-slate-500 mt-1">Notes: {appt.internalNotes}</p>
                          )}
                        </div>
                        <span className="text-[10px] font-bold px-2 py-1 rounded-lg bg-blue-100 text-blue-700">{appt.time}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <p className="text-xs text-slate-400 font-bold uppercase">Tooth-level treatments</p>
                {selectedPatient.toothHistory && selectedPatient.toothHistory.length > 0 ? (
                  <div className="space-y-2">
                    {selectedPatient.toothHistory.map(entry => (
                      <div key={`${entry.tooth}-${entry.date}-${entry.treatment}`} className="p-3 rounded-xl bg-slate-50 border">
                        <p className="text-sm font-bold">Tooth {entry.tooth} → {entry.treatment}</p>
                        <p className="text-xs text-slate-500">{entry.date} {entry.outcome ? `· ${entry.outcome}` : ''}</p>
                        {entry.notes && <p className="text-[11px] text-slate-500 mt-1">{entry.notes}</p>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-400">No tooth history logged.</p>
                )}
              </div>

              {selectedPatient.attachments.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-slate-400 font-bold uppercase">X-rays / Images</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedPatient.attachments.map(file => (
                      <a
                        key={file.id}
                        href={file.url}
                        className="px-3 py-2 rounded-xl border bg-slate-50 text-xs font-bold text-slate-700 hover:border-blue-300"
                      >
                        {file.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white rounded-3xl border shadow-sm p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-slate-400 uppercase">Panoramic Analysis</p>
                  <p className="font-black text-lg">DENTEX sample report</p>
                </div>
                <Icons.Image />
              </div>

              <div className="space-y-3">
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold text-slate-500 uppercase">Upload panoramic X-ray</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      setUploadFile(e.target.files?.[0] ?? null);
                      setUploadStatus('idle');
                      setUploadError('');
                    }}
                    className="text-xs"
                  />
                </div>
                <button
                  type="button"
                  onClick={handlePanoramicUpload}
                  disabled={uploadStatus === 'uploading'}
                  className="w-full px-4 py-2 rounded-xl bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 disabled:opacity-60"
                >
                  {uploadStatus === 'uploading' ? 'Running analysis...' : 'Run analysis'}
                </button>
                {uploadStatus === 'error' && (
                  <div className="text-xs text-rose-600 bg-rose-50 border border-rose-100 rounded-xl p-2">
                    {uploadError}
                  </div>
                )}
                {uploadStatus === 'success' && (
                  <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl p-2">
                    Analysis complete. Results added below.
                  </div>
                )}
              </div>

              {!latestStudy ? (
                <div className="rounded-2xl border border-dashed p-4 text-sm text-slate-500 bg-slate-50">
                  No panoramic study is linked to this patient yet.
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="p-3 rounded-2xl bg-slate-50">
                      <p className="text-xs font-bold text-slate-400 uppercase">Latest scan</p>
                      <p className="font-bold text-slate-800">{latestStudy.capturedAt}</p>
                      <p className="text-[11px] text-slate-500">Image: {latestStudy.fileName}</p>
                    </div>
                    <div className="p-3 rounded-2xl bg-slate-50">
                      <p className="text-xs font-bold text-slate-400 uppercase">Findings</p>
                      <p className="font-bold text-slate-800">{latestStudy.findings.length}</p>
                      <p className="text-[11px] text-slate-500">{latestStudy.width} x {latestStudy.height}px</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs font-bold text-slate-400 uppercase">Diagnosis mix</p>
                    {diagnosisSummary.length === 0 ? (
                      <p className="text-xs text-slate-400">No diagnoses detected.</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {diagnosisSummary.map(([label, count]) => (
                          <span key={label} className="px-3 py-1 rounded-lg bg-blue-50 text-blue-700 text-xs font-bold">
                            {label} · {count}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs font-bold text-slate-400 uppercase">Key findings</p>
                    {topFindings.length === 0 ? (
                      <p className="text-xs text-slate-400">No findings listed.</p>
                    ) : (
                      <div className="space-y-2">
                        {topFindings.map(finding => (
                          <div key={finding.id} className="p-3 rounded-xl bg-slate-50 border flex items-center justify-between">
                            <div>
                              <p className="text-sm font-bold">FDI {finding.fdiTooth}</p>
                              <p className="text-xs text-slate-500">
                                {finding.diagnosis} · Quadrant {finding.quadrant}
                                {typeof finding.score === 'number' ? ` · ${Math.round(finding.score * 100)}%` : ''}
                              </p>
                            </div>
                            <span className="text-[11px] font-bold px-2 py-1 rounded-lg bg-white border">
                              Area {Math.round(finding.area)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selectedAnalysis && latestStudy && (
                <div className="text-[11px] text-slate-500 leading-relaxed">
                  <p className="font-bold text-slate-600">Source</p>
                  <p>
                    {selectedAnalysis.model.name} · {latestStudy?.source.dataset} · {latestStudy?.source.license}
                  </p>
                  <p>
                    Dataset: {latestStudy?.source.url}
                  </p>
                  {selectedAnalysis.note && <p>{selectedAnalysis.note}</p>}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
